/**
 * AwaitingProvisioning — the holding screen for an account with no workspace yet.
 *
 * WHY THIS EXISTS. `handle_new_user` creates a profile with `workspace_id` NULL
 * (migration 0115), deliberately: defaulting every signup into one hardcoded workspace
 * would put the first real tenant's users somewhere they can read another tenant's
 * data. NULL is the honest value — a fresh signup genuinely has no workspace.
 *
 * The cost of that honesty is that such a user would otherwise sign in successfully and
 * land on a dashboard where every workspace-scoped query returns nothing. That reads as
 * a broken product. This screen is what makes the honest state legible.
 *
 * TONE — this is deliberately NOT an error. Nothing has failed: the account exists, the
 * password worked, and a normal step has not happened yet. So no red, no alert icon, no
 * `role="alert"`, and no apology. It says what is true, what happens next, and who to
 * ask — which is what someone in this state actually needs. Presenting a routine
 * provisioning gap as a failure teaches people to distrust real errors.
 */
import { Clock, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

export default function AwaitingProvisioning() {
  const { profile, signOut } = useAuth();

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="p-card" style={{ maxWidth: 460, textAlign: 'center' }}>
        <div
          aria-hidden="true"
          style={{
            width: 48, height: 48, borderRadius: '50%', margin: '0 auto var(--p-4)',
            display: 'grid', placeItems: 'center',
            background: 'rgba(94, 169, 240, 0.12)', color: 'var(--p-info)',
          }}
        >
          <Clock size={22} />
        </div>

        <h1 className="p-card-title" style={{ marginBottom: 'var(--p-2)' }}>
          Your account is being set up
        </h1>

        <p style={{ color: 'var(--p-text-2)', margin: 0, lineHeight: 1.6 }}>
          You’re signed in{profile?.email ? ` as ${profile.email}` : ''}, but you haven’t
          been added to a workspace yet. An administrator needs to do that before you can
          see anything.
        </p>

        <p style={{ color: 'var(--p-text-3)', marginTop: 'var(--p-3)', fontSize: 13, lineHeight: 1.6 }}>
          If you’re expecting access, contact whoever invited you and let them know your
          account is waiting. Once you’ve been added, sign out and back in.
        </p>

        <button
          type="button"
          onClick={signOut}
          className="p-btn-secondary p-focusable"
          style={{ marginTop: 'var(--p-4)', display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </div>
  );
}
